import { FallOutlined, RiseOutlined } from '@/components/icons';
import clsx from 'clsx';
import { CSSProperties } from 'react';
import SimpleBar from 'simplebar-react';
import { ActivityCard, NoData } from '../components';
import Filters from '../components/Filters';
import Head from '../components/Head';
import SkeletonLoader from '../components/SkeletonLoader';
import { useActivities } from '../hooks/useActivities';
import { useFilters } from '../hooks/useFilters';
import styles from '../style.module.css';
import {
  getActivitiesPerday,
  getActivityTotalDistancePerWeek,
  getActivityTotalTimePerWeek,
  getRunningActivities,
  initialValue,
  parseAllActivities,
  weekNumbers,
} from '../utils/activities';
import { orange } from '../utils/common';
import {
  days,
  formatHours,
  getDayMonth,
  getDayYearNumberFromDate,
  getTimeStamp,
} from '../utils/date';

const minCyclingRange_init = 0;
const maxCyclingRange_init = 300; // km
const minRunningRange_init = 0;
const maxRunningRange_init = 50; // km

const formatRunningKms = (distance: number) => {
  const sign = distance > 0 ? '+' : '';
  return sign + distance.toFixed(1);
};

const getColor = (distance: number) => {
  if (distance < 0) return 'red';
  if (distance > 0) return 'green';
  else return 'grey';
};

const Calendar = () => {
  const { activities, isLoading } = useActivities();

  const {
    sportIncludesFilter,
    runDistanceFilter,
    rideDistanceFilter,
    handleRunningRangeChange,
    handleCyclingRangeChange,
    displayVirtualRide,
    toggleDisplayVirtualRide,
    displaySwimming,
    toggleDisplaySwimming,
    displayCycling,
    toggleDisplayCycling,
    displayRunning,
    toggleDisplayRunning,
  } = useFilters(
    minRunningRange_init,
    maxRunningRange_init,
    minCyclingRange_init,
    maxCyclingRange_init
  );

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!activities) {
    return <NoData>No activities</NoData>;
  }

  // tri des activités par ordre chronologique
  activities.sort(
    (a, b) =>
      getTimeStamp(a.start_date_local) - getTimeStamp(b.start_date_local)
  );

  const activitiesPerDay = activities.reduce(getActivitiesPerday, {});

  // remplir les jours sans activités
  [...Array(365).keys()].forEach((day) => {
    day = day + 1; // on retire le zéro, on va jusque 366 jours
    if (!activitiesPerDay[day]) {
      activitiesPerDay[day] = [];
    }
  });

  const activitiesPerDayArray = Object.values(activitiesPerDay);

  const allActivities = activities.map(parseAllActivities);
  const runningACtivities = getRunningActivities(allActivities);

  const activitiesTotalTimePerWeek = allActivities.reduce(
    getActivityTotalTimePerWeek,
    {
      ...initialValue,
    }
  );

  const runningActivitiesTotalTimePerWeek = runningACtivities.reduce(
    getActivityTotalDistancePerWeek,
    { ...initialValue }
  );

  const totalHoursStats = Object.values(activitiesTotalTimePerWeek);
  const runningTotalKmStats = Object.values(runningActivitiesTotalTimePerWeek);
  const diffRunningPerCent = runningTotalKmStats.map((distance, index) => {
    if (index > 0 && distance > 0) {
      const perCent =
        (100 * (distance - runningTotalKmStats[index - 1])) / distance;
      return perCent;
    } else return 0;
  });

  return (
    <div id="calendar">
      <Head title="Strava Dashboard - calendar" />
      <Filters
        displayCycling={displayCycling}
        toggleDisplayCycling={toggleDisplayCycling}
        displayRunning={displayRunning}
        toggleDisplayRunning={toggleDisplayRunning}
        displaySwimming={displaySwimming}
        toggleDisplaySwimming={toggleDisplaySwimming}
        displayVirtualRide={displayVirtualRide}
        toggleDisplayVirtualRide={toggleDisplayVirtualRide}
        minCyclingRange_init={minCyclingRange_init}
        maxCyclingRange_init={maxCyclingRange_init}
        handleCyclingRangeChange={handleCyclingRangeChange}
        minRunningRange_init={minRunningRange_init}
        maxRunningRange_init={maxRunningRange_init}
        handleRunningRangeChange={handleRunningRangeChange}
      />
      <nav className={styles.today}>
        <a href="#currentDay">
          <p>Aujourd'hui</p>
        </a>
      </nav>
      <div className={styles.daysGridContainer}>
        <div className={styles.gridHeader}>Week</div>
        {days
          .slice(1)
          .concat(days[0])
          .map((day, index) => {
            return (
              <div key={index} className={styles.gridHeader}>
                {day}
              </div>
            );
          })}
      </div>
      <SimpleBar
        style={{
          height: 'calc(100vh - 300px)',
          scrollBehavior: 'smooth',
          scrollSnapType: 'y mandatory',
        }}
      >
        <div className={styles.mainGridContainer}>
          <div className={styles.weekGridContainer}>
            {weekNumbers.map((week, index) => {
              const distance = diffRunningPerCent[index];
              const color = getColor(distance);
              const icon =
                distance > 0 ? (
                  <RiseOutlined style={{ color }} />
                ) : (
                  <FallOutlined style={{ color }} />
                );
              return (
                <div key={index} className={styles.weekContainer}>
                  <div className={styles.week}>{week}</div>
                  <div>
                    <b>Temps total</b>
                  </div>
                  <div>{formatHours(totalHoursStats[index])}</div>
                  <div style={{ marginTop: '20px' }}>
                    <b>Kms courus</b>
                  </div>
                  <div>{runningTotalKmStats[index]} km</div>
                  <div>
                    <span style={{ color: getColor(distance) }}>
                      {formatRunningKms(distance)}%
                    </span>{' '}
                    {icon}
                  </div>
                </div>
              );
            })}
          </div>
          <div className={styles.cardsGridContainer}>
            {activitiesPerDayArray.map((activities, index) => {
              const date = new Date();
              const firstDay = new Date(date.getFullYear(), 0, 0).getDay() + 1; // (dimanche 1er janvier -> 6)
              const todayIndex = getDayYearNumberFromDate(date.toString()) - 1;
              const getStyle = (index: number): CSSProperties => {
                switch (index) {
                  case 0:
                    return { gridColumnStart: firstDay };
                  case todayIndex:
                    return { border: `4px solid ${orange}` };
                  default:
                    return {};
                }
              };
              const isCurrentDay = index === todayIndex;
              return (
                <div
                  key={index}
                  className={styles.gridItem}
                  style={getStyle(index)}
                  data-testid={`day-${index + 1}`}
                >
                  <div
                    className={clsx(styles.dayNumber, {
                      [styles.currentDay]: isCurrentDay,
                    })}
                    id={isCurrentDay ? 'currentDay' : `day-${index + 1}`}
                  >
                    {getDayMonth(index + 1)}
                  </div>
                  {activities
                    .filter(sportIncludesFilter)
                    .filter(rideDistanceFilter)
                    .filter(runDistanceFilter)
                    .map((activity) => (
                      <ActivityCard key={activity.id} activity={activity} />
                    ))}
                </div>
              );
            })}
          </div>
        </div>
      </SimpleBar>
    </div>
  );
};

export default Calendar;
